import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileCheck, AlertTriangle, CheckCircle2, XCircle, Download, 
  Send, Eye, RefreshCw, Building2, FileText, Shield, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EInvoice = () => {
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [lhdnFormat, setLhdnFormat] = useState(null);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    
    // Fetch each endpoint separately with timeout to avoid hanging
    const timeout = (ms) => new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms));
    
    try {
      // Compliance status
      const complianceRes = await Promise.race([
        axios.get(`${API}/lhdn/compliance-status`),
        timeout(10000)
      ]).catch(() => ({ data: { status: 'not_registered', compliance_score: 0, checklist: [], message: 'Unable to load compliance status' } }));
      setComplianceStatus(complianceRes.data);
      
      // Invoices
      const invoicesRes = await Promise.race([
        axios.get(`${API}/invoices`),
        timeout(10000)
      ]).catch(() => ({ data: [] }));
      setInvoices(invoicesRes.data);
      
      // Submission history  
      const historyRes = await Promise.race([
        axios.get(`${API}/lhdn/submission-history`),
        timeout(10000)
      ]).catch(() => ({ data: [] }));
      setSubmissionHistory(historyRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load some e-Invoice data');
    } finally {
      setLoading(false);
    }
  };

  const validateInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/validate-lhdn`);
      setValidationResult(res.data);
      setShowValidationDialog(true);
    } catch (error) {
      toast.error('Failed to validate invoice');
    }
  };

  const viewLHDNFormat = async (invoiceId, format = 'json') => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/lhdn-format?format=${format}`);
      setLhdnFormat(res.data);
      setShowFormatDialog(true);
    } catch (error) {
      toast.error('Failed to get LHDN format');
    }
  };

  const submitEInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/submit-einvoice`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit e-Invoice');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      paid: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      overdue: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    };
    return <Badge className={styles[status] || styles.draft}>{status?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-primary">Loading e-Invoice data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="einvoice-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white flex items-center gap-3">
            <FileCheck className="w-8 h-8 text-primary" />
            LHDN E-Invoice
          </h1>
          <p className="text-muted-foreground">Malaysia MyInvois compliance management</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-orbit-border">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Compliance Status Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className={`bg-orbit-card border-orbit-border ${complianceStatus?.status === 'compliant' ? 'border-emerald-500/30' : 'border-amber-500/30'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${complianceStatus?.status === 'compliant' ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                  {complianceStatus?.status === 'compliant' ? (
                    <Shield className="w-6 h-6 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-white">Compliance Status</CardTitle>
                  <CardDescription>{complianceStatus?.message}</CardDescription>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Compliance Score</p>
                <p className={`text-3xl font-heading font-bold ${complianceStatus?.compliance_score >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {complianceStatus?.compliance_score || 0}%
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {complianceStatus?.checklist?.map((item, idx) => (
                <div key={idx} className="bg-orbit-bg/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {item.status === 'complete' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : item.status === 'incomplete' ? (
                      <XCircle className="w-4 h-4 text-rose-400" />
                    ) : (
                      <Info className="w-4 h-4 text-amber-400" />
                    )}
                    <span className="text-sm font-medium text-white">{item.item}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate" title={item.value}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Tabs defaultValue="invoices" className="space-y-6">
        <TabsList className="bg-orbit-card border border-orbit-border">
          <TabsTrigger value="invoices">Pending Invoices</TabsTrigger>
          <TabsTrigger value="history">Submission History</TabsTrigger>
        </TabsList>

        {/* Pending Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="bg-orbit-card border-orbit-border">
            <CardHeader>
              <CardTitle className="text-white">Invoices Ready for E-Invoice Submission</CardTitle>
              <CardDescription>Validate and submit invoices to LHDN MyInvois system</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-orbit-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Invoice #</TableHead>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">E-Invoice</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.filter(inv => !inv.e_invoice_submitted).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No pending invoices for e-Invoice submission
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.filter(inv => !inv.e_invoice_submitted).map((invoice) => (
                      <TableRow key={invoice.id} className="border-orbit-border">
                        <TableCell className="font-mono text-white">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-white">{invoice.customer_name}</TableCell>
                        <TableCell className="font-mono text-white">{formatCurrency(invoice.total)}</TableCell>
                        <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-gray-500/30 text-gray-400">
                            Not Submitted
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => validateInvoice(invoice.id)}
                              className="text-muted-foreground hover:text-white"
                            >
                              <FileCheck className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLHDNFormat(invoice.id)}
                              className="text-muted-foreground hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => submitEInvoice(invoice.id)}
                              className="bg-primary hover:bg-primary/90"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Submit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submission History Tab */}
        <TabsContent value="history">
          <Card className="bg-orbit-card border-orbit-border">
            <CardHeader>
              <CardTitle className="text-white">E-Invoice Submission History</CardTitle>
              <CardDescription>Track your submitted e-Invoices to LHDN</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-orbit-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Invoice #</TableHead>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-muted-foreground">E-Invoice UUID</TableHead>
                    <TableHead className="text-muted-foreground">Submitted</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissionHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No e-Invoices submitted yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissionHistory.map((item) => (
                      <TableRow key={item.invoice_id} className="border-orbit-border">
                        <TableCell className="font-mono text-white">{item.invoice_number}</TableCell>
                        <TableCell className="text-white">{item.customer_name}</TableCell>
                        <TableCell className="font-mono text-white">{formatCurrency(item.total)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {item.e_invoice_uuid?.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(item.submitted_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLHDNFormat(item.invoice_id, 'json')}
                              className="text-muted-foreground hover:text-white"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              JSON
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLHDNFormat(item.invoice_id, 'xml')}
                              className="text-muted-foreground hover:text-white"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              XML
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Validation Result Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="bg-orbit-card border-orbit-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {validationResult?.is_valid ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <XCircle className="w-5 h-5 text-rose-400" />
              )}
              Validation Result
            </DialogTitle>
            <DialogDescription>
              LHDN e-Invoice compliance check for invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-orbit-bg rounded-lg">
              <span className="text-muted-foreground">Compliance Score</span>
              <span className={`text-2xl font-bold ${validationResult?.compliance_score >= 80 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {validationResult?.compliance_score}%
              </span>
            </div>

            {validationResult?.errors?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-rose-400">Errors</h4>
                {validationResult.errors.map((error, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-rose-300 bg-rose-500/10 p-2 rounded">
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    {error}
                  </div>
                ))}
              </div>
            )}

            {validationResult?.warnings?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-amber-400">Warnings</h4>
                {validationResult.warnings.map((warning, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 p-2 rounded">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    {warning}
                  </div>
                ))}
              </div>
            )}

            {validationResult?.is_valid && (
              <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-500/10 p-3 rounded">
                <CheckCircle2 className="w-5 h-5" />
                Invoice is ready for LHDN e-Invoice submission
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* LHDN Format Dialog */}
      <Dialog open={showFormatDialog} onOpenChange={setShowFormatDialog}>
        <DialogContent className="bg-orbit-card border-orbit-border max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              LHDN E-Invoice Format ({lhdnFormat?.format?.toUpperCase()})
            </DialogTitle>
            <DialogDescription>
              MyInvois compliant invoice data
            </DialogDescription>
          </DialogHeader>
          <div className="bg-orbit-bg rounded-lg p-4 overflow-auto max-h-[60vh]">
            <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
              {lhdnFormat?.format === 'xml' 
                ? lhdnFormat?.content 
                : JSON.stringify(lhdnFormat?.content, null, 2)}
            </pre>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              className="border-orbit-border"
              onClick={() => {
                const content = lhdnFormat?.format === 'xml' 
                  ? lhdnFormat?.content 
                  : JSON.stringify(lhdnFormat?.content, null, 2);
                navigator.clipboard.writeText(content);
                toast.success('Copied to clipboard');
              }}
            >
              Copy to Clipboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EInvoice;
